<template>
    <div v-if="loaded">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :max-date="additionals.dataAtual"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.unidades"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="model.unidadeId === null"
                            :options="infraestruturas"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="model.infraestruturaId === null"
                            :options="centrosCusto"
                            default-option-text="Todos"
                            label="Setor"
                            show-default-option
                            text-field="setorNome"
                            v-model="model.centroCustoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.fornecedores"
                            default-option-text="Todos"
                            label="Fornecedor"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.fornecedorId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.grupos"
                            default-option-text="Todos"
                            label="Grupo"
                            show-default-option
                            text-field="nome"
                            v-model="model.grupoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="model.grupoId === null"
                            :options="subgrupos"
                            default-option-text="Todos"
                            label="Subgrupo"
                            show-default-option
                            text-field="nome"
                            v-model="model.subgrupoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input-autocomplete
                            :repository="repositories.material"
                            :request-query-string-params="autocompleteQueryStringParams.material"
                            :search-fields="autocompleteFields.material"
                            label="Material"
                            text-field="descricaoCompleta"
                            uppercase
                            v-model="model.material"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="exportTo"
                    color="info"
                    small
                    title="Exportar"
                >
                    <rkm-icon icon="file-export" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :foot-clone="itens.length > 0"
                :items="itens"
                no-footer-sorting
                sort-by="dataHoraEstorno"
            >
                <template #cell(codigoMaterial)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell(lote)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell(validade)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell(subgrupo)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #foot(dataHoraEstorno)>
                    <span></span>
                </template>

                <template #foot(codigoMaterial)>
                    <span></span>
                </template>

                <template #foot(material)>
                    <span></span>
                </template>

                <template #foot(lote)>
                    <span></span>
                </template>

                <template #foot(validade)>
                    <span></span>
                </template>

                <template #foot(grupo)>
                    <span></span>
                </template>

                <template #foot(subgrupo)>
                    <span></span>
                </template>

                <template #foot(dataDocumento)>
                    <span></span>
                </template>

                <template #foot(numeroDocumento)>
                    <span></span>
                </template>

                <template #foot(tipoDocumento)>
                    <span></span>
                </template>

                <template #foot(fornecedor)>
                    <span></span>
                </template>

                <template #foot(usuarioEstorno)>
                    <span></span>
                </template>

                <template #foot(quantidade)>
                    <span>{{ getSafely(() => totalizadores.quantidade, 0) }}</span>
                </template>
            </rkm-table>
        </rkm-area>
    </div>
</template>

<script>
import formatsFilter from '@/filters/formats'
import { mapActions } from 'vuex'
import { repository, error, data, object, stringCase, file } from '@/mixins'

export default {
    computed: {
        autocompleteQueryStringParams() {

            return this.filterNotNull({
                material: {
                    grupo_id: this.model.grupoId,
                    subgrupo_id: this.model.subgrupoId,
                }
            })
        },
        autocompleteFields() {

            return {
                material: [
                    { key: 'codigo_material', searchable: true },
                    { key: 'descricao', searchable: true },
                ],
            }
        },
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {

            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        validations() {

            return {
                periodo: { required: true },
            }
        },
        infraestruturas() {

            if (this.model.unidadeId !== null) {

                return this.form.unidades.find(u => u.id === this.model.unidadeId).infraestruturas
            }

            return []
        },
        centrosCusto() {

            if (this.model.infraestruturaId !== null && this.infraestruturas.length > 0) {

                const infrasestrutura = this.infraestruturas.find(i => i.id === this.model.infraestruturaId)

                return infrasestrutura ? infrasestrutura.centrosCusto : []
            }

            return []
        },
        subgrupos() {

            if (this.model.grupoId !== null) {

                return this.form.grupos.find(g => g.id === this.model.grupoId).subgrupos
            }

            return []
        },
    },
    created() {

        this.repositories = {
            relatorioEstornoEntrada: this.getConectaSusRepository('almoxarifado.relatorioEstornoEntrada'),
            material: this.getConectaSusRepository('cadastro.material'),
            impressao: this.getConectaSusRepository('impressao.almoxarifado'),
            exportacao: this.getConectaSusRepository('exportacao.almoxarifado'),
        }

        this.load()
    },
    data() {

        return {
            loaded: false,
            additionals: null,
            form: null,
            defaultModel: null,
            model: null,
            selectedAll: false,
            indeterminate: true,
            selectedCols: [
                'codigoMaterial',
                'lote',
                'validade',
            ],
            columns: [
                {
                    key: 'dataHoraEstorno',
                    label: 'Data/hora do estorno',
                    sortable: true,
                    class: 'col-min text-center',
                    formatter: value => this.formatDateTime(value),
                    filterByFormatted: true,
                    fixed: true
                },
                { key: 'codigoMaterial', label: 'Código do material', sortable: true, class: 'col-min text-center' },
                { key: 'material', label: 'Material', sortable: true, fixed: true },
                { key: 'lote', label: 'Lote', sortable: true, class: 'col-min text-center' },
                {
                    key: 'validade',
                    label: 'Validade',
                    sortable: true,
                    class: 'col-min text-center',
                    formatter: value => this.isNullOrEmptyString(value) ? '' : this.formatDate(value),
                    filterByFormatted: true,
                },
                { key: 'grupo', label: 'Grupo', sortable: true },
                { key: 'subgrupo', label: 'Subgrupo', sortable: true },
                {
                    key: 'dataDocumento',
                    label: 'Data do documento',
                    sortable: true,
                    class: 'col-min text-center',
                    formatter: value => this.formatDate(value),
                    filterByFormatted: true,
                },
                { key: 'numeroDocumento', label: 'Número do documento', sortable: true, class: 'col-min text-center' },
                { key: 'tipoDocumento', label: 'Tipo do documento', sortable: true, class: 'col-min text-center' },
                { key: 'fornecedor', label: 'Fornecedor', sortable: true },
                { key: 'usuarioEstorno', label: 'Usuário que realizou o estorno', sortable: true },
                { key: 'quantidade', label: 'Quantidade', sortable: true, class: 'col-min text-center', fixed: true }
            ],
            itens: [],
            totalizadores: null
        }
    },
    methods: {
        ...formatsFilter,
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            this.repositories.relatorioEstornoEntrada.list()
                .then(({ data }) => {

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model
                    this.defaultModel = this.clone(data.model)

                    this.addWatchers()

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {

            this.$watch('model.unidadeId', function () {

                this.model.infraestruturaId = null
            })

            this.$watch('model.infraestruturaId', function () {

                this.model.centroCustoId = null
            })

            this.$watch('model.grupoId', function () {

                this.model.material = null
                this.model.subgrupoId = null
            })

            this.$watch('model.subgrupoId', function () {

                this.model.material = null
            })
        },
        search() {

            this.setLoading()

            this.repositories.relatorioEstornoEntrada.index(this.prepareData())
                .then(({ data }) => {

                    this.itens = data.estornos
                    this.totalizadores = data.totalizadores
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {

            this.model = this.clone(this.defaultModel)

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        print() {

            this.setLoading()

            this.repositories.impressao.relatorioEstornoEntrada(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {

            this.setLoading()

            this.repositories.exportacao.relatorioEstornoEntrada(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        prepareDataWithColumns() {

            const data = this.prepareData()

            data.selectedCols = this.fields.map(f => ({
                key: this.camelToSnake(f.key),
                label: f.label,
                class: f.class
            }))

            return data
        },
        prepareData() {

            const data = this.clone(this.model)

            this.formateDates(data.periodo, ['start', 'end'], 'DD/MM/YYYY')
            this.createIdKeysToObject(data, ['material'])

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        selectAll(selected) {

            this.selectedCols = selected ? this.columns.map(c => c.key) : []
        },
    },
    mixins: [repository, error, data, object, stringCase, file],
    watch: {
        model: {
            deep: true,
            handler() {

                this.itens = []
                this.totalizadores = null
            }
        },
        selectedCols(newValue) {

            this.selectedAll = newValue.length > 0 && newValue.length == this.columns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.columns.length
        },
    }
}
</script>

<style scoped>
.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 400px;
}
</style>